import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';

const resolveInitialValue = <T,>(init: T | (() => T)): T =>
  typeof init === 'function' ? (init as () => T)() : init;

const readStoredValue = <T,>(
  key: string,
  init: T | (() => T),
  persist: boolean
): T => {
  if (!persist) return resolveInitialValue(init);

  try {
    const stored = window.localStorage.getItem(key);
    if (stored !== null) return JSON.parse(stored) as T;
    return resolveInitialValue(init);
  } catch (e) {
    console.error(`useLocalStorage: failed to read key="${key}"`, e);
    return resolveInitialValue(init);
  }
};

function useLocalStorage<T>(
  key: string,
  init: T | (() => T),
  persist: boolean = true
): [T, Dispatch<SetStateAction<T>>, () => void, () => void] {
  const [entry, setEntry] = useState<{ key: string; persist: boolean; value: T }>(() => ({
    key,
    persist,
    value: readStoredValue(key, init, persist),
  }));

  // Rehydrate on key changes before paint. Without this guard, a value read
  // from the previous key can be saved into the next key during login/user-key
  // resolution, which makes stale title/body drafts appear unexpectedly.
  useLayoutEffect(() => {
    setEntry((prev) => {
      if (prev.key === key && prev.persist === persist) return prev;
      return {
        key,
        persist,
        value: readStoredValue(key, init, persist),
      };
    });
  }, [key, init, persist]);

  const setValue = useCallback<Dispatch<SetStateAction<T>>>(
    (next) => {
      setEntry((prev) => {
        const nextValue =
          typeof next === 'function'
            ? (next as (prevState: T) => T)(prev.value)
            : next;

        if (
          prev.key === key &&
          prev.persist === persist &&
          Object.is(prev.value, nextValue)
        ) {
          return prev;
        }

        return { key, persist, value: nextValue };
      });
    },
    [key, persist]
  );

  useEffect(() => {
    if (!persist) return;
    if (entry.key !== key || entry.persist !== persist) return;

    try {
      window.localStorage.setItem(key, JSON.stringify(entry.value));
    } catch (e) {
      console.error(`useLocalStorage: failed to write key="${key}"`, e);
    }
  }, [entry.key, entry.persist, entry.value, key, persist]);

  useEffect(() => {
    if (persist) return;
    if (entry.key !== key) return;

    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      console.error(`useLocalStorage: failed to remove key="${key}"`, e);
    }
  }, [entry.key, key, persist]);

  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key !== key) return;

      try {
        const nextValue = e.newValue
          ? (JSON.parse(e.newValue) as T)
          : resolveInitialValue(init);
        setEntry((prev) =>
          prev.key === key ? { key, persist, value: nextValue } : prev
        );
      } catch (err) {
        console.error(`useLocalStorage: storage event parse error key="${key}"`, err);
      }
    };

    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, [key, init, persist]);

  const reset = useCallback(() => {
    const value = resolveInitialValue(init);
    setEntry({ key, persist, value });

    try {
      if (persist) {
        window.localStorage.setItem(key, JSON.stringify(value));
      } else {
        window.localStorage.removeItem(key);
      }
    } catch (e) {
      console.error(`useLocalStorage: failed to reset key="${key}"`, e);
    }
  }, [key, init, persist]);

  const remove = useCallback(() => {
    try {
      window.localStorage.removeItem(key);
    } catch (e) {
      console.error(`useLocalStorage: failed to remove key="${key}"`, e);
    }
  }, [key]);

  return [entry.value, setValue, reset, remove];
}

export default useLocalStorage;
